import { Button, Typography } from '@pebl/ui';
import log from 'loglevel';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ApiResourceAction, ApiResourceActionRelatedAction } from '@/api/types';
import RoutePathConstants from '@/entrypoints/guest-website/routing/route-path-constants';
import { mapApiResourceUrlToResource } from '@/utils/map-api-resource-url-to-resource';

import { mapActionOptionsToComponent } from '../../utils/map-action-options';

type ActionFormBuilderProps = {
  /**
   * The action to render the form for.
   * If no action is provided or it's undefined then nothing is rendered.
   */
  action?: ApiResourceAction;

  /**
   * Is the action execution currently pending from a previous request?
   * If so then the form should likely prevent re-submission and indicate
   * that it is pending.
   */
  isPending?: boolean;

  /**
   * A callback to invoke when the form want to submit, I.E. call this when
   * the submit button is clicked.
   *
   * @param formData Any form data that should be submit with the action.
   */
  submitAction?: (formData: unknown) => void;

  /**
   * A callback function that is called if the action is cancelled via
   * any cancel mechanic the form may provide (or the form is closed etc).
   */
  onActionCancelled?: () => void;

  /**
   * If `true`, the related actions will not be shown in the form.
   * @default false - The related actions will be displayed using the
   * relatedActionsPlacement prop and renderRelatedAction prop if supplied,
   * otherwise as a simple list of links.
   */
  hideRelatedActions?: boolean;

  /**
   * Where to render any related action links in relation to the forms
   * submit/cancel buttons.
   *
   * 'above' - Renders the related action links above the Submit button but below all the form fields.
   * 'below' - Renders the related action links below the Submit/Cance buttons.
   *
   * @default 'below' - Render the related actions below the form.
   */
  relatedActionsPlacement?: 'above' | 'below';

  /**
   * A function to give us flexibility in how we render the related actions if we so desire.
   * This allows you to provide a custom render function that will be run against each related
   * action and render it however you wish.
   *
   * Returning `undefined` from this function will result in the related action not being rendered.
   *
   * By default (if this prop is omitted) related actions are rendered as a simple list of links.
   *
   * @param action The related action to render.
   * @returns A React node to render the related action, or `undefined` to not render it.
   */
  renderRelatedAction?: (
    relatedAction: ApiResourceActionRelatedAction,
  ) => React.ReactNode | undefined;
};

/**
 * This is the type for the custom reference we expose via the useImperativeHandle
 * call in the ActionFormBuilder. The main purpose of this is to expose form specific
 * methods (such as setting/clearing errors on fields) to the parent component where
 * the form is actually being processed.
 */
export type ActionFormBuilderRef = {
  /**
   * Set a specific error on a field in the form.
   */
  setError: (name: string, error: { type: string; message: string }) => void;
  /**
   * Clear errors from the form.
   */
  clearErrors: (name?: string | string[]) => void;
  /**
   * Reset the form.
   */
  reset: () => void;
};

export const ActionFormBuilder = forwardRef<
  ActionFormBuilderRef,
  ActionFormBuilderProps
>(function ActionFormBuilder(
  {
    action,
    hideRelatedActions,
    isPending,
    onActionCancelled,
    relatedActionsPlacement = 'below',
    renderRelatedAction,
    submitAction,
  }: ActionFormBuilderProps,
  ref,
) {
  // eslint-disable-next-line react-compiler/react-compiler
  'use no memo';

  const { t } = useTranslation();
  const formMethods = useForm({
    mode: 'onBlur',
  });
  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
  } = formMethods;

  /*
   * TODO: When the action changes (chained actions) we need to reset the form, we could either
   *       do that here with reset() in a useEffect, OR add reset to the imperativeHandle
   *       and then let the parent component call it when it actually changes the action
   *       due to a changed action.
   */

  useImperativeHandle<ActionFormBuilderRef, ActionFormBuilderRef>(
    ref,
    () => ({
      clearErrors,
      setError,
      reset,
    }),
    [clearErrors, setError, reset],
  );

  const onSubmitAction = useCallback(
    (formData: unknown) => {
      // Clear any existing "custom" errors we have set from a previous request.
      clearErrors();

      // Pass it up to be handled by the parent component.
      submitAction?.(formData);
    },
    [clearErrors, submitAction],
  );

  const relatedActionsElements = useMemo(() => {
    if (hideRelatedActions || !action?.related_actions?.length) {
      return false;
    }

    return action?.related_actions?.map((relatedAction) => {
      const resource = mapApiResourceUrlToResource(relatedAction.resource); // e.g. ['account']
      const actionType = relatedAction.action; // e.g. 'forgot_password'

      if (!resource) {
        log.warn(
          `Failed to map related action URL to resource: ${relatedAction.resource}:${relatedAction.action}`,
          relatedAction,
        );
        return false;
      }

      // Target = /resources/:resourceId/actions/:actionId
      const targetPath = RoutePathConstants.RESOURCE_ACTION.link({
        resourceId: encodeURIComponent(resource.join('/')),
        actionId: actionType,
      });

      return renderRelatedAction ? (
        renderRelatedAction(relatedAction)
      ) : (
        <Link to={targetPath} key={relatedAction.resource}>
          <Typography className="underline" variant="body1">
            {relatedAction.title}
          </Typography>
        </Link>
      );
    });
  }, [action?.related_actions, hideRelatedActions, renderRelatedAction]);

  if (!action) {
    return false;
  }

  return (
    <FormProvider {...formMethods}>
      <form
        aria-label="action-form" // Needed for tests
        onSubmit={handleSubmit(onSubmitAction)}
        className="flex flex-col flex-wrap gap-4"
      >
        {Object.entries(action?.options?.properties ?? {}).map(
          ([key, value]) => {
            const isPropertyRequired =
              action?.options?.required?.includes(key) ?? false;
            const isPropertyInError = errors[key] !== undefined ? true : false;
            const propertyErrorMessage = isPropertyInError
              ? errors[key]?.message
              : undefined;

            return (
              <div key={key} className="">
                {mapActionOptionsToComponent(key, value, {
                  required: isPropertyRequired,
                  isError: isPropertyInError,
                  errorMessage: propertyErrorMessage?.toString(), // ! This toString() is sketchy!!
                })}
              </div>
            );
          },
        )}

        {relatedActionsPlacement === 'above' && relatedActionsElements}

        <Button
          data-testid="action-form-submit"
          type="submit"
          isLoading={isPending}
          disabled={isPending || action?.disabled}
          loadingText={
            action?.submit ??
            action?.text ??
            t('actions.default_submit_button_pending')
          }
          className="w-full"
        >
          {action?.submit ?? action?.text ?? t('actions.default_submit_button')}
        </Button>

        {action?.cancel && (
          <Button
            data-testid="action-form-cancel"
            type="button"
            onClick={onActionCancelled}
            className={`w-full`}
            variant="outline"
          >
            {action?.cancel}
          </Button>
        )}
      </form>

      {relatedActionsPlacement === 'below' && relatedActionsElements}
    </FormProvider>
  );
});
